import React from 'react';
import { X, Package, Calendar, User, Truck, Box } from 'lucide-react';

const OrderDetailModal = ({ isOpen, onClose, order, productos = [] }) => {
  if (!isOpen || !order) return null;

  const calculateOrderTotal = () => {
    return order.detalles?.reduce((sum, detalle) => {
      return sum + (detalle.precio_total || 0);
    }, 0) || 0;
  };

  const calculateTotalCajas = () => {
    return order.detalles?.reduce((sum, detalle) => {
      return sum + (detalle.cantidad_cajas || 0);
    }, 0) || 0;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Detalle del Pedido</h2>
            <p className="modal-subtitle">Pedido #{order.id}</p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          {/* Información General */}
          <div className="info-section">
            <h3 className="section-title">Información General</h3>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-icon">
                  <User size={20} />
                </div>
                <div>
                  <div className="info-label">Cliente</div>
                  <div className="info-value">{order.cliente_nombre || 'N/A'}</div>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <Calendar size={20} />
                </div>
                <div>
                  <div className="info-label">Fecha Despacho</div>
                  <div className="info-value">
                    {order.fecha_despacho 
                      ? new Date(order.fecha_despacho + 'T00:00:00').toLocaleDateString('es-CL')
                      : 'N/A'}
                  </div>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <Truck size={20} />
                </div>
                <div>
                  <div className="info-label">Tipo Despacho</div>
                  <div className="info-value">{order.tipo_despacho || 'N/A'}</div>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <Truck size={20} />
                </div>
                <div>
                  <div className="info-label">Transporte</div>
                  <div className="info-value">
                    {order.transportes?.nombre || 'No asignado'}
                    {order.transportes?.tipo && (
                      <span className={`transport-badge ${order.transportes.tipo === 'exportacion' ? 'badge-exportacion' : 'badge-nacional'}`}>
                        {order.transportes.tipo === 'exportacion' ? 'Exportación' : 'Nacional'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <Calendar size={20} />
                </div>
                <div>
                  <div className="info-label">Temporada / Semanas</div>
                  <div className="info-value">
                    {order.temporada} / S{order.semana_inicio}-{order.semana_fin}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Productos */}
          <div className="products-section">
            <h3 className="section-title">Productos ({order.detalles?.length || 0})</h3>
            <div className="products-list">
              {order.detalles?.map((detalle, index) => {
                const producto = detalle.producto || {};
                return (
                  <div key={index} className="product-card">
                    <div className="product-header">
                      <div className="product-number">#{index + 1}</div>
                      <div className="product-name">
                        {producto.nombre || 'Producto no encontrado'}
                      </div>
                    </div>
                    
                    <div className="product-details">
                      <div className="detail-row">
                        <span className="detail-label">Código:</span>
                        <span className="detail-value">{producto.code || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Tipo Caja:</span>
                        <span className="detail-value">{producto.tipo_caja?.tipo || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Cantidad Cajas:</span>
                        <span className="detail-value font-bold">{detalle.cantidad_cajas}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Precio Unitario:</span>
                        <span className="detail-value">${detalle.precio_unitario?.toFixed(2)}</span>
                      </div>
                      <div className="detail-row total-row">
                        <span className="detail-label">Total Línea:</span>
                        <span className="detail-value total-value">
                          ${detalle.precio_total?.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {producto.bunches_box && (
                      <div className="product-extra">
                        <div className="extra-item">
                          <Box size={14} />
                          <span>{producto.bunches_box} ramos/caja</span>
                        </div>
                        <div className="extra-item">
                          <Package size={14} />
                          <span>{detalle.cantidad_cajas * producto.bunches_box} ramos totales</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Resumen */}
          <div className="summary-section">
            <div className="summary-row">
              <span className="summary-label">Total Cajas:</span>
              <span className="summary-value">{calculateTotalCajas()}</span>
            </div>
            <div className="summary-row total">
              <span className="summary-label">Total Pedido:</span>
              <span className="summary-value">${calculateOrderTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: var(--spacing-4);
          }

          .modal-container {
            background: white;
            border-radius: var(--radius-lg);
            width: 100%;
            max-width: 800px;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: var(--spacing-6);
            border-bottom: 1px solid var(--gray-200);
          }

          .modal-title {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: var(--gray-900);
            margin: 0;
          }

          .modal-subtitle {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            margin: var(--spacing-1) 0 0 0;
          }

          .modal-close {
            padding: var(--spacing-2);
            border: none;
            background: transparent;
            cursor: pointer;
            color: var(--gray-500);
            border-radius: var(--radius-md);
            transition: all 0.2s;
          }

          .modal-close:hover {
            background-color: var(--gray-100);
            color: var(--gray-700);
          }

          .modal-body {
            padding: var(--spacing-6);
            overflow-y: auto;
            flex: 1;
          }

          .info-section {
            margin-bottom: var(--spacing-6);
          }

          .section-title {
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-900);
            margin-bottom: var(--spacing-4);
          }

          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: var(--spacing-4);
          }

          .info-item {
            display: flex;
            align-items: flex-start;
            gap: var(--spacing-3);
            padding: var(--spacing-4);
            background-color: var(--gray-50);
            border-radius: var(--radius-lg);
          }

          .info-icon {
            width: 40px;
            height: 40px;
            background-color: var(--primary-blue);
            color: white;
            border-radius: var(--radius-lg);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .info-label {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            margin-bottom: var(--spacing-1);
          }

          .info-value {
            font-size: var(--font-size-base);
            font-weight: 600;
            color: var(--gray-900);
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
          }
          
          .transport-badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            width: fit-content;
            margin-top: 4px;
          }
          
          .badge-exportacion {
            background-color: rgba(59, 130, 246, 0.1);
            color: #2563eb;
            border: 1px solid rgba(59, 130, 246, 0.2);
          }
          
          .badge-nacional {
            background-color: rgba(34, 197, 94, 0.1);
            color: #16a34a;
            border: 1px solid rgba(34, 197, 94, 0.2);
          }

          .products-section {
            margin-bottom: var(--spacing-6);
          }

          .products-list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-4);
          }

          .product-card {
            border: 2px solid var(--gray-200);
            border-radius: var(--radius-lg);
            padding: var(--spacing-4);
            background-color: white;
          }

          .product-header {
            display: flex;
            align-items: center;
            gap: var(--spacing-3);
            margin-bottom: var(--spacing-3);
            padding-bottom: var(--spacing-3);
            border-bottom: 1px solid var(--gray-200);
          }

          .product-number {
            width: 32px;
            height: 32px;
            background-color: var(--primary-blue);
            color: white;
            border-radius: var(--radius-md);
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: var(--font-size-sm);
          }

          .product-name {
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-900);
          }

          .product-details {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
            margin-bottom: var(--spacing-3);
          }

          .detail-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-2) 0;
          }

          .detail-row.total-row {
            border-top: 2px solid var(--gray-200);
            padding-top: var(--spacing-3);
            margin-top: var(--spacing-2);
          }

          .detail-label {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
          }

          .detail-value {
            font-size: var(--font-size-base);
            color: var(--gray-900);
          }

          .detail-value.font-bold {
            font-weight: 600;
          }

          .total-value {
            font-size: var(--font-size-lg);
            font-weight: 700;
            color: var(--secondary-green);
          }

          .product-extra {
            display: flex;
            gap: var(--spacing-4);
            padding-top: var(--spacing-3);
            border-top: 1px solid var(--gray-200);
            font-size: var(--font-size-sm);
            color: var(--gray-600);
          }

          .extra-item {
            display: flex;
            align-items: center;
            gap: var(--spacing-1);
          }

          .summary-section {
            background-color: var(--gray-100);
            border-radius: var(--radius-lg);
            padding: var(--spacing-4);
          }

          .summary-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-2) 0;
          }

          .summary-row.total {
            border-top: 2px solid var(--gray-300);
            padding-top: var(--spacing-3);
            margin-top: var(--spacing-2);
          }

          .summary-label {
            font-size: var(--font-size-base);
            color: var(--gray-700);
          }

          .summary-row.total .summary-label {
            font-size: var(--font-size-lg);
            font-weight: 600;
          }

          .summary-value {
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-900);
          }

          .summary-row.total .summary-value {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: var(--secondary-green);
          }

          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-3);
            padding: var(--spacing-6);
            border-top: 1px solid var(--gray-200);
          }

          @media (max-width: 768px) {
            .modal-container {
              max-width: 100%;
              max-height: 100vh;
              border-radius: 0;
            }

            .info-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderDetailModal;
